import React from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
  Button,
} from "@chakra-ui/react";
import { ArrowUpIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { FESTIVAL } from "./data/festival";

const FooterHeading = ({ children }) => (
  <Heading
    as="h2"
    fontSize="lg"
    color="#FFD52E"
    mb={3}
    fontFamily="'Poppins', sans-serif !important"
    fontWeight="800 !important"
    letterSpacing="0.08em !important"
    textTransform="uppercase"
  >
    {children}
  </Heading>
);

const footerLinkStyle = {
  color: "white",
  opacity: 0.9,
  fontWeight: "600",
  _hover: { opacity: 1, color: "#FFD52E", textDecoration: "none" },
};

const SiteFooter = () => (
  <Box
    as="footer"
    bg="#063F74"
    color="white"
    pt={{ base: 12, md: 16 }}
    pb={8}
    position="relative"
    overflow="hidden"
    _before={{
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: "linear-gradient(90deg, #0C7FBC 0%, #FFD52E 42%, #F47A22 72%, #D73522 100%)",
      pointerEvents: "none",
    }}
    _after={{
      content: '""',
      position: "absolute",
      top: "4px",
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: "url('/brand/arts-festival-hero-bg.webp')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      opacity: 0.16,
      pointerEvents: "none",
    }}
  >
    <Container maxW="container.xl" position="relative" zIndex={1}>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={{ base: 10, md: 12 }}>
        <VStack align={{ base: "center", md: "flex-start" }} spacing={4} textAlign={{ base: "center", md: "left" }}>
          <Image
            src="/brand/arts-festival-logo.webp"
            alt="Culver City Arts Festival"
            maxW="220px"
            loading="lazy"
            decoding="async"
            filter="drop-shadow(0 6px 14px rgba(0,0,0,0.3))"
          />
          <Text fontWeight="800" fontSize="lg">
            {FESTIVAL.dateLabel}
          </Text>
          <Text opacity={0.9}>
            {FESTIVAL.street}, {FESTIVAL.district}
            <br />
            Free for all ages · {FESTIVAL.hoursLabel}
          </Text>
        </VStack>

        <Box textAlign={{ base: "center", md: "left" }}>
          <FooterHeading>Plan Your Day</FooterHeading>
          <VStack as="ul" listStyleType="none" align={{ base: "center", md: "flex-start" }} spacing={2} m={0}>
            <li><Link as={RouterLink} to="/entertainment" {...footerLinkStyle}>Lineup &amp; Set Times</Link></li>
            <li><Link as={RouterLink} to="/about" {...footerLinkStyle}>About &amp; Getting Here</Link></li>
            <li><Link as={RouterLink} to="/map" {...footerLinkStyle}>Festival Map</Link></li>
            <li><Link as={RouterLink} to="/international-zone" {...footerLinkStyle}>International Arts Zone</Link></li>
          </VStack>
        </Box>

        <Box textAlign={{ base: "center", md: "left" }}>
          <FooterHeading>Get Involved</FooterHeading>
          <VStack as="ul" listStyleType="none" align={{ base: "center", md: "flex-start" }} spacing={2} m={0}>
            <li>
              <Link href={FESTIVAL.vendorUrl} isExternal {...footerLinkStyle}>
                Vendor Sign-Up <ExternalLinkIcon mx="2px" />
              </Link>
            </li>
            <li>
              <Link href={FESTIVAL.volunteerUrl} isExternal {...footerLinkStyle}>
                Volunteer <ExternalLinkIcon mx="2px" />
              </Link>
            </li>
            <li><Link as={RouterLink} to="/work-with-us" {...footerLinkStyle}>Work With Us</Link></li>
          </VStack>
        </Box>

        <Box textAlign={{ base: "center", md: "left" }}>
          <FooterHeading>Support the Festival</FooterHeading>
          <Text opacity={0.9} mb={4}>
            The festival is free for everyone. Your donation helps keep the art, music, and family fun on the street.
          </Text>
          <Button
            as="a"
            href={FESTIVAL.donateUrl}
            target="_blank"
            rel="noopener noreferrer"
            bg="#F47A22"
            color="white"
            border="2px solid #FFD52E"
            borderRadius="14px"
            fontWeight="900"
            _hover={{ bg: "#D73522", transform: "translateY(-2px)" }}
          >
            Donate
          </Button>
        </Box>
      </SimpleGrid>

      <Flex
        mt={{ base: 10, md: 14 }}
        pt={6}
        borderTop="1px solid rgba(255,255,255,0.2)"
        direction={{ base: "column", sm: "row" }}
        justify="space-between"
        align="center"
        gap={4}
      >
        <Text opacity={0.85} fontSize="sm" textAlign={{ base: "center", sm: "left" }}>
          &copy; 2026 Culver City Arts Festival. All rights reserved.
        </Text>
        <Button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          variant="ghost"
          color="white"
          size="sm"
          rightIcon={<ArrowUpIcon />}
          _hover={{ bg: "rgba(255,255,255,0.15)" }}
        >
          Back to top
        </Button>
      </Flex>
    </Container>
  </Box>
);

export default SiteFooter;
